import React, { useRef, useState, useEffect } from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import { Calendar, X } from 'lucide-react';

/**
 * Format ISO date string (YYYY-MM-DD) into MM/DD/YYYY
 */
const formatToMMDDYYYY = (isoStr) => {
    if (!isoStr || typeof isoStr !== 'string') return '';
    const parts = isoStr.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
        const [year, month, day] = parts;
        return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }
    return isoStr;
};

/**
 * Parse MM/DD/YYYY into ISO date string (YYYY-MM-DD)
 */
const parseToYYYYMMDD = (displayStr) => {
    if (!displayStr || typeof displayStr !== 'string') return '';
    const clean = displayStr.trim();
    const parts = clean.split('/');
    if (parts.length === 3) {
        const [month, day, year] = parts;
        if (year.length === 4 && month.length > 0 && day.length > 0) {
            const m = parseInt(month, 10);
            const d = parseInt(day, 10);
            const y = parseInt(year, 10);
            if (!isNaN(m) && !isNaN(d) && !isNaN(y) && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
        }
    }
    return '';
};

/**
 * DateFilterInput
 * 
 * Custom date input component that presents:
 * - Upper-cased label (e.g., "START DATE", "END DATE")
 * - Uppercase placeholder: "MM/DD/YYYY"
 * - Formatted date display in MM/DD/YYYY format
 * - Native date picker launcher via showPicker()
 * - Clean calendar icon & optional quick-clear icon
 * - Unchanged underlying date payload in standard YYYY-MM-DD format
 */
const DateFilterInput = ({
    label = "DATE",
    value = "",
    onChange,
    min,
    max,
    sx = {},
    disabled = false
}) => {
    const hiddenDateInputRef = useRef(null);
    const [displayValue, setDisplayValue] = useState(formatToMMDDYYYY(value));

    // Keep display value in sync with external value prop
    useEffect(() => {
        setDisplayValue(formatToMMDDYYYY(value));
    }, [value]);

    const triggerChange = (nextIso) => {
        if (!onChange) return;
        onChange({
            target: { value: nextIso || '' },
            currentTarget: { value: nextIso || '' }
        });
    };

    const handleOpenPicker = (e) => {
        if (disabled) return;
        if (e && e.stopPropagation) e.stopPropagation();
        try {
            if (hiddenDateInputRef.current && typeof hiddenDateInputRef.current.showPicker === 'function') {
                hiddenDateInputRef.current.showPicker();
            } else if (hiddenDateInputRef.current) {
                hiddenDateInputRef.current.focus();
            }
        } catch (err) {
            if (hiddenDateInputRef.current) {
                hiddenDateInputRef.current.focus();
            }
        }
    };

    const handleNativeDateChange = (e) => {
        const iso = e.target.value; // YYYY-MM-DD
        setDisplayValue(formatToMMDDYYYY(iso));
        triggerChange(iso);
    };

    const handleTextChange = (e) => {
        const val = e.target.value;
        setDisplayValue(val);

        if (!val.trim()) {
            triggerChange('');
            return;
        }

        // Auto-commit when full MM/DD/YYYY is typed
        if (val.length === 10) {
            const iso = parseToYYYYMMDD(val);
            if (iso) {
                triggerChange(iso);
            }
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setDisplayValue('');
        triggerChange('');
        if (hiddenDateInputRef.current) {
            hiddenDateInputRef.current.value = '';
        }
    };

    const formattedLabel = (label || '').toUpperCase();

    return (
        <Box sx={{ position: 'relative', width: '100%', minWidth: 130, ...sx }}>
            {/* Hidden native date input for reliable OS/Browser calendar picker */}
            <input
                type="date"
                ref={hiddenDateInputRef}
                value={value || ''}
                min={min}
                max={max}
                disabled={disabled}
                onChange={handleNativeDateChange}
                style={{
                    position: 'absolute',
                    opacity: 0,
                    width: 0,
                    height: 0,
                    pointerEvents: 'none',
                    zIndex: -1,
                    top: 0,
                    left: 0
                }}
                tabIndex={-1}
                aria-hidden="true"
            />

            <TextField
                fullWidth
                size="small"
                label={formattedLabel}
                placeholder="MM/DD/YYYY"
                value={displayValue}
                disabled={disabled}
                onChange={handleTextChange}
                onClick={handleOpenPicker}
                InputLabelProps={{
                    shrink: true,
                    sx: {
                        textTransform: 'uppercase',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: '#64748B',
                        letterSpacing: '0.04em',
                        '&.Mui-focused': {
                            color: '#FFBD65',
                            fontWeight: 600,
                        }
                    }
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ mr: -0.5 }}>
                            {displayValue && !disabled && (
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={handleClear}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: 'none',
                                        bgcolor: 'transparent',
                                        p: 0.25,
                                        mr: 0.5,
                                        cursor: 'pointer',
                                        color: '#94A3B8',
                                        borderRadius: '50%',
                                        '&:hover': { color: '#803927' }
                                    }}
                                    title="Clear date"
                                >
                                    <X size={14} />
                                </Box>
                            )}
                            <Box
                                component="button"
                                type="button"
                                onClick={handleOpenPicker}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    p: 0.25,
                                    cursor: 'pointer',
                                    color: '#64748B',
                                    '&:hover': { color: '#1E293B' }
                                }}
                                title="Select date"
                            >
                                <Calendar size={16} />
                            </Box>
                        </InputAdornment>
                    ),
                    sx: {
                        borderRadius: '8px',
                        height: '40px',
                        backgroundColor: '#FFFFFF',
                        boxShadow: 'none',
                        cursor: 'pointer',
                        '& input': {
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            color: '#1E293B',
                            fontWeight: 400,
                            py: 0,
                            height: '40px',
                            boxSizing: 'border-box',
                            '&::placeholder': {
                                color: '#94A3B8',
                                opacity: 1,
                                textTransform: 'uppercase',
                                fontSize: '0.85rem',
                                letterSpacing: '0.02em',
                            }
                        },
                        '& fieldset': {
                            borderColor: '#E2E8F0',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#CBD5E1 !important',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#FFBD65 !important',
                            borderWidth: '1px !important',
                        },
                    }
                }}
            />
        </Box>
    );
};

export default DateFilterInput;
